import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { FaArrowLeft, FaSave, FaCheck, FaTimes, FaChevronDown, FaChevronUp, FaCheckCircle, FaRegCircle, FaLink } from "react-icons/fa";
import ReactTooltip from "react-tooltip";
import Loader from "../../../../components/loader";
import api from "../../../../services/api";
import { TRAINING_TEST_QUESTIONS } from "./test-questions";

// Key Performance Indicators - Outcomes that predict success
const TRAINING_KPI_CHECKLIST = [
  {
    key: "ships_independently",
    label: "Ships code independently (5+ PRs merged with minimal supervision)",
    category: "Deliverables",
    predictive: "Can deliver without hand-holding",
  },
  {
    key: "explains_reasoning",
    label: "Can explain WHY behind architecture choices (not just HOW)",
    category: "Skills",
    predictive: "Deep understanding vs surface copying",
  },
  {
    key: "proactive_communication",
    label: "Proactive communication (updates before being asked, flags blockers early)",
    category: "Culture",
    predictive: "Won't drop balls with clients",
  },
  {
    key: "self_reviews_quality",
    label: "Self-reviews before submitting (clean code, no console.logs, tests edge cases)",
    category: "Deliverables",
    predictive: "Quality bar without supervision",
  },
  {
    key: "realistic_estimates",
    label: "Estimates realistically and improves over time (within 2x by end of training)",
    category: "Culture",
    predictive: "Can set client expectations accurately",
  },
  {
    key: "applies_feedback_fast",
    label: "Applies feedback immediately (doesn't repeat same mistakes)",
    category: "Culture",
    predictive: "Fast learner, coachable",
  },
  {
    key: "asks_smart_questions",
    label: "Asks clarifying questions upfront (edge cases, priorities, scope)",
    category: "Culture",
    predictive: "Avoids rework, reduces client back-and-forth",
  },
  {
    key: "production_ready_code",
    label: "Code is production-ready (error handling, edge cases, follows patterns)",
    category: "Skills",
    predictive: "Won't create technical debt",
  },
  {
    key: "handles_pressure_well",
    label: "Handles pressure professionally (deadlines, feedback, uncertainty)",
    category: "Culture",
    predictive: "Won't crack under client pressure",
  },
  {
    key: "no_handholding_needed",
    label: "Self-sufficient by Week 3 (works independently, knows when to ask)",
    category: "Culture",
    predictive: "Ready for real client work",
  },
];

// Training-specific evaluation criteria aligned with training repo content
const TRAINING_CRITERIA = {
  // DELIVERABLES (40% weight)
  gitWorkflow: {
    label: "Git Workflow & PR Practices",
    description: "One PR per feature, proper branching, clear commit messages",
    1: "• No understanding of Git workflow\n• Multiple features in one PR\n• Unclear commit messages",
    2: "• Basic Git knowledge\n• Sometimes mixes features in PRs\n• Inconsistent commit messages",
    3: "• Follows one PR per feature\n• Proper branch naming\n• Clear commit messages (feat:, fix:, refactor:)",
    4: "• Excellent PR practices\n• Self-reviews before submitting\n• Helps others with Git workflow",
    5: "• Sets team standards\n• Creates Git workflow documentation\n• Mentors others",
  },
  codeQuality: {
    label: "Code Quality & Patterns",
    description: "Architecture patterns, error handling, code readability",
    1: "• No patterns followed\n• No error handling\n• Unreadable code",
    2: "• Some patterns followed\n• Basic error handling\n• Code needs improvement",
    3: "• Follows architecture patterns (Controllers/Services)\n• Good error handling\n• Readable code",
    4: "• Excellent pattern usage\n• Comprehensive error handling\n• Code used as example",
    5: "• Creates new patterns\n• Prevents bugs across team\n• Code is team standard",
  },
  featureCompleteness: {
    label: "Feature Completeness",
    description: "Full-stack implementation, working end-to-end",
    1: "• Incomplete features\n• Only backend or only frontend\n• Broken functionality",
    2: "• Mostly complete\n• Some missing pieces\n• Basic functionality works",
    3: "• Full-stack & working\n• Complete features\n• All requirements met",
    4: "• Exceeds requirements\n• Handles edge cases\n• Production-ready",
    5: "• Exceptional quality\n• Anticipates needs\n• Sets new standards",
  },
  
  // SKILLS (30% weight)
  architectureUnderstanding: {
    label: "Architecture Understanding",
    description: "Controllers vs Services vs Webhooks vs Crons vs Scripts",
    1: "• No understanding of architecture\n• Mixes everything together\n• Cannot explain choices",
    2: "• Basic understanding\n• Sometimes confuses patterns\n• Limited explanation",
    3: "• Understands architecture patterns\n• Can explain WHY, not just HOW\n• Makes correct choices",
    4: "• Deep understanding\n• Explains reasoning clearly\n• Teaches others",
    5: "• Architecture expert\n• Creates new patterns\n• Sets company standards",
  },
  apiPatterns: {
    label: "API Patterns",
    description: "POST /search pattern, consistent responses, proper routing",
    1: "• No API patterns\n• Inconsistent responses\n• Messy routing",
    2: "• Knows POST /search\n• Sometimes follows patterns\n• Basic consistency",
    3: "• Always POST /search for filtering\n• Consistent {ok, data} responses\n• Proper routing",
    4: "• Excellent API design\n• Sets API standards\n• Refactors messy routes",
    5: "• API framework creator\n• Company-wide standards\n• Industry best practices",
  },
  frontendPatterns: {
    label: "Frontend Patterns",
    description: "Component structure, API service layer, state management",
    1: "• No structure\n• Mixed logic\n• Manual state updates",
    2: "• Basic structure\n• Some separation\n• Learning patterns",
    3: "• Clear component boundaries\n• API service layer implemented\n• Proper state management",
    4: "• Perfect scoping\n• Zero coupling\n• Excellent patterns",
    5: "• Creates frontend architecture\n• Framework-level patterns\n• Team standard",
  },
  databasePatterns: {
    label: "Database & Models",
    description: "MongoDB models, schemas, proper data structure",
    1: "• No schema understanding\n• Poor data structure\n• No validation",
    2: "• Basic schemas\n• Some validation\n• Needs improvement",
    3: "• Good model schemas\n• Proper validation\n• Makes sense",
    4: "• Excellent schemas\n• Comprehensive validation\n• Optimized structure",
    5: "• Database expert\n• Creates patterns\n• Performance optimized",
  },
  integrations: {
    label: "External Integrations",
    description: "Google Calendar, Webhooks, Email (Brevo), APIs",
    1: "• Cannot integrate APIs\n• Struggles with documentation\n• No webhook understanding",
    2: "• Basic API integration\n• Follows docs\n• Simple integrations",
    3: "• Integrates external APIs well\n• Understands webhooks\n• Email integration works",
    4: "• Complex integrations\n• Webhooks, OAuth\n• Production-ready",
    5: "• Integration master\n• Any API quickly\n• Creates integration patterns",
  },
  
  // CULTURE (30% weight)
  communication: {
    label: "Communication & Proactivity",
    description: "Proactive updates, clear communication, asks questions",
    1: "• Poor communication\n• No proactive updates\n• Doesn't ask questions",
    2: "• Basic communication\n• Some updates\n• Asks when stuck",
    3: "• Proactive updates\n• Clear communication\n• Asks good questions",
    4: "• Excellent communication\n• Anticipates needs\n• Helps others communicate",
    5: "• Communication champion\n• Sets standards\n• Transforms team culture",
  },
  ownership: {
    label: "Ownership & Responsibility",
    description: "Takes ownership, end-to-end responsibility, no boxes",
    1: "• \"Not my job\" mentality\n• Leaves broken code\n• Blames others",
    2: "• Fixes own bugs\n• Takes some responsibility\n• Basic ownership",
    3: "• Takes full ownership\n• End-to-end responsibility\n• No boxes attitude",
    4: "• Exceptional ownership\n• Owns client relationship\n• Never drops the ball",
    5: "• Legendary reliability\n• Client trusts completely\n• Prevents problems",
  },
  learningVelocity: {
    label: "Learning Velocity",
    description: "How quickly they learn, apply feedback, improve",
    1: "• Slow to learn\n• Doesn't apply feedback\n• Repeats mistakes",
    2: "• Moderate learning\n• Some feedback applied\n• Some improvement",
    3: "• Fast learner\n• Applies feedback immediately\n• Shows improvement",
    4: "• Exceptional learning\n• Anticipates feedback\n• Rapid improvement",
    5: "• Learning champion\n• Teaches others\n• Sets learning standards",
  },
};

const PILLARS = [
  {
    id: "deliverables",
    name: "Deliverables",
    weight: 40,
    fields: ["gitWorkflow", "codeQuality", "featureCompleteness"],
  },
  {
    id: "skills",
    name: "Skills",
    weight: 30,
    fields: ["architectureUnderstanding", "apiPatterns", "frontendPatterns", "databasePatterns", "integrations"],
  },
  {
    id: "culture",
    name: "Culture",
    weight: 30,
    fields: ["communication", "ownership", "learningVelocity"],
  },
];

export default function Evaluation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");
  const [gatekeeper, setGatekeeper] = useState({});
  const [performancePrediction, setPerformancePrediction] = useState(null);
  const [redFlags, setRedFlags] = useState("");
  const [standoutAchievements, setStandoutAchievements] = useState("");
  const [practicalEvidence, setPracticalEvidence] = useState({
    tickets: [],
    trackerEntries: [],
    loading: true,
  });
  const [expandedTestQuestions, setExpandedTestQuestions] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (user) {
      loadPracticalEvidence();
    }
  }, [user]);

  const loadPracticalEvidence = async () => {
    if (!user) return;
    try {
      const startDate = user.training_start_date || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      
      let tickets = [];

      // Load tracker entries
      const trackerRes = await api.post("/newcomer_tracker/search", { 
        user_id: id, 
        limit: 100 
      });
      let trackerEntries = [];
      if (trackerRes.ok) {
        if (Array.isArray(trackerRes.data)) {
          trackerEntries = trackerRes.data;
        } else if (trackerRes.data?.entries) {
          trackerEntries = trackerRes.data.entries;
        } else if (trackerRes.data?.data) {
          trackerEntries = trackerRes.data.data;
        }
      }
      
      setPracticalEvidence({
        tickets,
        trackerEntries,
        loading: false,
      });
    } catch (error) {
      console.log(error);
      setPracticalEvidence(prev => ({ ...prev, loading: false }));
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const userRes = await api.get(`/user/${id}`);
      if (!userRes.ok) {
        toast.error("User not found");
        navigate("/selecole/tracker");
        return;
      }
      setUser(userRes.data);
      setNotes(userRes.data.training_evaluation_notes || "");
      if (userRes.data.training_gatekeeper) setGatekeeper(userRes.data.training_gatekeeper);
      if (userRes.data.training_performance_prediction) setPerformancePrediction(userRes.data.training_performance_prediction);
      if (userRes.data.training_red_flags) setRedFlags(userRes.data.training_red_flags);
      if (userRes.data.training_standout_achievements) setStandoutAchievements(userRes.data.training_standout_achievements);
      
      // Load existing evaluation scores (prefer manual, fallback to test scores)
      if (userRes.data.training_evaluation_scores) {
        setScores(userRes.data.training_evaluation_scores);
      } else if (userRes.data.training_test_evaluation_scores) {
        // Pre-populate from test results if no manual evaluation yet
        setScores(userRes.data.training_test_evaluation_scores);
        toast.success("Scores pre-populated from test results. You can adjust them based on practical work.");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (field, value) => {
    setScores({ ...scores, [field]: value });
  };

  const toggleGatekeeper = async (key) => {
    const newGatekeeper = { ...gatekeeper, [key]: !gatekeeper[key] };
    setGatekeeper(newGatekeeper);
    
    try {
      const { ok } = await api.put(`/user/${id}`, { training_gatekeeper: newGatekeeper });
      if (!ok) {
        toast.error("Failed to save");
        setGatekeeper(gatekeeper); // Revert
      }
    } catch (error) {
      console.log(error);
      toast.error("Error saving");
      setGatekeeper(gatekeeper); // Revert
    }
  };

  const calculateAverages = () => {
    const deliverables = PILLARS.find(p => p.id === "deliverables");
    const skills = PILLARS.find(p => p.id === "skills");
    const culture = PILLARS.find(p => p.id === "culture");

    const deliverablesScores = deliverables.fields
      .map(f => scores[f])
      .filter(s => s !== undefined && s !== null);
    const deliverablesAvg = deliverablesScores.length > 0
      ? deliverablesScores.reduce((a, b) => a + b, 0) / deliverablesScores.length
      : 0;

    const skillsScores = skills.fields
      .map(f => scores[f])
      .filter(s => s !== undefined && s !== null);
    const skillsAvg = skillsScores.length > 0
      ? skillsScores.reduce((a, b) => a + b, 0) / skillsScores.length
      : 0;

    const cultureScores = culture.fields
      .map(f => scores[f])
      .filter(s => s !== undefined && s !== null);
    const cultureAvg = cultureScores.length > 0
      ? cultureScores.reduce((a, b) => a + b, 0) / cultureScores.length
      : 0;

    const overall = deliverablesAvg * 0.4 + skillsAvg * 0.3 + cultureAvg * 0.3;

    let grade = "Basic";
    if (overall >= 4.6) grade = "Exceptional";
    else if (overall >= 3.6) grade = "Advanced";
    else if (overall >= 2.6) grade = "Intermediate";
    else if (overall >= 1.6) grade = "Basic";
    else grade = "Poor";

    return { deliverablesAvg, skillsAvg, cultureAvg, overall, grade };
  };

  const saveEvaluation = async () => {
    setSaving(true);
    try {
      const { deliverablesAvg, skillsAvg, cultureAvg, overall, grade } = calculateAverages();
      
      const { ok } = await api.put(`/user/${id}`, {
        training_evaluation_scores: scores,
        training_evaluation_score: overall,
        training_evaluation_deliverables: deliverablesAvg,
        training_evaluation_skills: skillsAvg,
        training_evaluation_culture: cultureAvg,
        training_evaluation_grade: grade,
        training_evaluation_notes: notes,
        training_performance_prediction: performancePrediction,
        training_red_flags: redFlags,
        training_standout_achievements: standoutAchievements,
        training_evaluation_completed_at: new Date().toISOString(),
      });

      if (!ok) return toast.error("Failed to save evaluation");
      
      toast.success("Evaluation saved!");
      navigate("/selecole/tracker");
    } catch (error) {
      console.log(error);
      toast.error("Error saving evaluation");
    } finally {
      setSaving(false);
    }
  };

  const graduateUser = async () => {
    if (!confirm(`Graduate ${user?.name}? This will mark them as graduated.`)) return;
    
    try {
      const { ok } = await api.put(`/user/${id}`, {
        training_status: "graduated",
        training_graduated_at: new Date().toISOString(),
      });
      if (!ok) return toast.error("Failed to graduate");
      toast.success(`🎉 ${user.name} has graduated!`);
      navigate("/selecole/tracker");
    } catch (error) {
      toast.error("Error");
    }
  };

  const resetTest = async () => {
    if (!confirm(`Reset test for ${user?.name}? This will clear their test answers and allow them to retake it.`)) return;
    
    try {
      const { ok } = await api.put(`/user/${id}`, {
        training_test_answers: {},
        training_test_results: {},
        training_test_completed_at: null,
        training_test_evaluation_scores: {},
      });
      if (!ok) return toast.error("Failed to reset test");
      toast.success("Test reset! They can now retake it.");
      await loadData(); // Reload to refresh state
    } catch (error) {
      console.error(error);
      toast.error("Error resetting test");
    }
  };

  const copyTestLink = () => {
    const baseUrl = window.location.origin;
    const testLink = `${baseUrl}/selecole/training-test/${id}`;
    navigator.clipboard.writeText(testLink);
    toast.success(`Test link copied! Send this to ${user.name}.`);
  };


  if (loading) return <Loader />;
  if (!user) return <div className="p-8 text-center text-gray-500">User not found</div>;

  const { deliverablesAvg, skillsAvg, cultureAvg, overall, grade } = calculateAverages();
  const gradeColors = {
    Exceptional: "bg-purple-100 text-purple-700 border-purple-500",
    Advanced: "bg-teal-100 text-teal-700 border-teal-500",
    Intermediate: "bg-blue-100 text-blue-700 border-blue-500",
    Basic: "bg-yellow-100 text-yellow-700 border-yellow-500",
    Poor: "bg-red-100 text-red-700 border-red-500",
  };

  const hasTestResults = user?.training_test_completed_at && user?.training_test_results;

  const toggleTestQuestions = (fieldKey) => {
    setExpandedTestQuestions(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  // Group test questions by pillar and criterion
  const getTestQuestionsByCriterion = () => {
    const grouped = {};
    TRAINING_TEST_QUESTIONS.forEach(q => {
      if (!grouped[q.criterion]) {
        grouped[q.criterion] = [];
      }
      grouped[q.criterion].push(q);
    });
    return grouped;
  };

  // Map tickets/PRs to evaluation criteria
  const getTicketsForCriterion = (fieldKey) => {
    if (!practicalEvidence.tickets || practicalEvidence.tickets.length === 0) return [];
    
    const keywordMap = {
      gitWorkflow: ['pr', 'pull request', 'merge', 'branch', 'commit'],
      codeQuality: ['refactor', 'clean', 'quality', 'improve', 'pattern'],
      featureCompleteness: ['feature', 'implement', 'add', 'create', 'build'],
      architectureUnderstanding: ['architecture', 'structure', 'service', 'controller'],
      apiPatterns: ['api', 'endpoint', 'route'],
      frontendPatterns: ['frontend', 'component', 'ui', 'page'],
      databasePatterns: ['database', 'model', 'schema'],
      integrations: ['integration', 'stripe', 'google', 'webhook'],
      communication: ['documentation', 'readme', 'docs'],
      ownership: ['fix', 'bug', 'issue', 'hotfix'],
      learningVelocity: ['improve', 'refactor', 'optimize'],
    };

    const keywords = keywordMap[fieldKey] || [];
    if (keywords.length === 0) return [];

    return practicalEvidence.tickets.filter(ticket => {
      const title = (ticket.title || '').toLowerCase();
      const description = (ticket.description || '').toLowerCase();
      const searchText = `${title} ${description}`;
      
      return keywords.some(keyword => searchText.includes(keyword.toLowerCase()));
    }).slice(0, 3); // Limit to 3 most relevant
  };

  const testAnswers = user?.training_test_answers || {};
  const questionsByCriterion = getTestQuestionsByCriterion();

  // Map test criteria to evaluation criteria for display
  const criterionMapping = {
    timeToShip: "gitWorkflow",
    productThinking: "featureCompleteness",
    codeQuality: "codeQuality",
    problemDifficulty: "architectureUnderstanding",
    codeReadability: "codeQuality",
    apiPatterns: "apiPatterns",
    frontendPatterns: "frontendPatterns",
    complexityManagement: "architectureUnderstanding",
    problemSolving: "architectureUnderstanding",
    endToEndResponsibility: "ownership",
    noBoxes: "ownership",
    commercialMindset: "ownership",
    shippingVelocity: "gitWorkflow",
    pmfObsession: "ownership",
    featureJudgment: "featureCompleteness",
    technicalRestraint: "featureCompleteness",
    communicationClarity: "communication",
    learningVelocity: "learningVelocity",
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate("/selecole/tracker")}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <FaArrowLeft className="text-gray-500" />
          </button>
          
          <div className="flex items-center gap-3">
            <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full border-2 border-white shadow" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Evaluate {user.name}</h1>
              <p className="text-sm text-gray-500">Training Evaluation</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!user?.training_test_completed_at && (
            <button
              onClick={copyTestLink}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <FaLink /> Copy Test Link
            </button>
          )}
          
          {/* Summary Scores */}
          <div className="flex items-center gap-4 bg-white rounded-lg border border-gray-200 px-4 py-2">
            <div className="text-center">
              <div className="text-xs text-gray-500">Overall</div>
              <div className={`text-2xl font-bold ${gradeColors[grade]?.split(' ')[1] || 'text-gray-700'}`}>
                {overall.toFixed(1)}
              </div>
            </div>
            <div className="h-8 w-px bg-gray-200" />
            <div className="text-center">
              <div className="text-xs text-gray-500">Grade</div>
              <div className={`text-sm font-semibold px-2 py-1 rounded ${gradeColors[grade] || ''}`}>
                {grade}
              </div>
            </div>
          </div>

          <button
            onClick={saveEvaluation}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <FaSave /> {saving ? "Saving..." : "Save Evaluation"}
          </button>
          
          {user?.training_test_completed_at && (
            <button
              onClick={resetTest}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Reset Test
            </button>
          )}
          <button
            onClick={graduateUser}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Graduate
          </button>
        </div>
      </div>

      {/* Test Results Summary (if available) */}
      {hasTestResults && (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-blue-900">Test Results</h3>
              <span className="text-sm text-blue-700">
                Score: {user.training_test_results.correct}/{user.training_test_results.totalQuestions} 
                ({Math.round((user.training_test_results.correct / user.training_test_results.totalQuestions) * 100)}%)
              </span>
            </div>
            <p className="text-sm text-blue-800 mb-2">
              Test scores have been used to pre-populate evaluation scores. Review and adjust based on practical work.
            </p>
            <div className="grid grid-cols-3 gap-3 mt-3">
              {(() => {
                // Recalculate from actual test answers to avoid bad stored data
                const testAnswers = user?.training_test_answers || {};
                const pillarStats = {
                  deliverables: { correct: 0, total: 0 },
                  skills: { correct: 0, total: 0 },
                  culture: { correct: 0, total: 0 },
                };
                
                TRAINING_TEST_QUESTIONS.forEach(q => {
                  if (pillarStats[q.pillar]) {
                    pillarStats[q.pillar].total++;
                    if (testAnswers[q.id] === q.correctAnswer) {
                      pillarStats[q.pillar].correct++;
                    }
                  }
                });
                
                return Object.entries(pillarStats).map(([pillar, stats]) => {
                  const percentage = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
                  return (
                    <div key={pillar} className="bg-white rounded p-2">
                      <div className="text-xs text-gray-600 capitalize mb-1">{pillar}</div>
                      <div className="text-sm font-semibold text-gray-900">{stats.correct}/{stats.total} ({percentage}%)</div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

        </>
      )}

      {/* Training Performance Indicators */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Training Performance Indicators</h2>
            <p className="text-sm text-gray-500 mt-1">Key outcomes that predict post-training performance</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              {TRAINING_KPI_CHECKLIST.filter(kpi => gatekeeper[kpi.key]).length}/{TRAINING_KPI_CHECKLIST.length}
            </div>
            <div className="text-xs text-gray-500">Achieved</div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {TRAINING_KPI_CHECKLIST.map((kpi) => {
            const isChecked = gatekeeper[kpi.key];
            const categoryColor = 
              kpi.category === "Deliverables" ? "text-amber-600 bg-amber-50" :
              kpi.category === "Skills" ? "text-blue-600 bg-blue-50" :
              "text-teal-600 bg-teal-50";

            return (
              <button
                key={kpi.key}
                onClick={() => toggleGatekeeper(kpi.key)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  isChecked 
                    ? "border-green-500 bg-green-50" 
                    : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {isChecked ? (
                      <FaCheckCircle className="text-green-600 text-xl" />
                    ) : (
                      <FaRegCircle className="text-gray-300 text-xl" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className={`text-sm font-medium ${isChecked ? "text-gray-900" : "text-gray-700"}`}>
                        {kpi.label}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${categoryColor} flex-shrink-0`}>
                        {kpi.category}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 italic">
                      → Predicts: {kpi.predictive}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pillar Scores Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { name: "Deliverables", score: deliverablesAvg, weight: 40, color: "bg-amber-500" },
          { name: "Skills", score: skillsAvg, weight: 30, color: "bg-blue-500" },
          { name: "Culture", score: cultureAvg, weight: 30, color: "bg-teal-500" },
        ].map((pillar) => (
          <div key={pillar.name} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">{pillar.name}</span>
              <span className="text-xs text-gray-500">{pillar.weight}%</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">
              {pillar.score.toFixed(1)}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`${pillar.color} h-2 rounded-full transition-all`}
                style={{ width: `${(pillar.score / 5) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Evaluation Criteria */}
      {PILLARS.map((pillar) => (
        <div key={pillar.id} className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">{pillar.name}</h2>
            <span className="text-sm text-gray-500">Weight: {pillar.weight}%</span>
          </div>

          <div className="space-y-6">
            {pillar.fields.map((fieldKey) => {
              const criteria = TRAINING_CRITERIA[fieldKey];
              const currentScore = scores[fieldKey];

              return (
                <div key={fieldKey} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-medium text-gray-900">{criteria.label}</h3>
                        {hasTestResults && user.training_test_evaluation_scores?.[fieldKey] && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-semibold">
                            Test Score: {user.training_test_evaluation_scores[fieldKey]}/5
                          </span>
                        )}
                        {currentScore && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-semibold">
                            Your Rating: {currentScore}/5
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mb-3">{criteria.description}</p>
                      
                      {/* Show related test questions for this criterion */}
                      {hasTestResults && (() => {
                        const relatedTestCriteria = Object.entries(criterionMapping)
                          .filter(([_, evalCriterion]) => evalCriterion === fieldKey)
                          .map(([testCriterion]) => testCriterion);
                        
                        if (relatedTestCriteria.length > 0) {
                          const relatedQuestions = TRAINING_TEST_QUESTIONS.filter(q => 
                            relatedTestCriteria.includes(q.criterion)
                          );
                          const correctCount = relatedQuestions.filter(q => {
                            const answer = testAnswers[q.id];
                            return answer !== undefined && answer === q.correctAnswer;
                          }).length;
                          
                          return (
                            <div className="text-xs text-gray-600 mb-2">
                              Related test questions: {correctCount}/{relatedQuestions.length} correct
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map((score) => {
                        const testScore = hasTestResults ? user.training_test_evaluation_scores?.[fieldKey] : null;
                        const isTestScore = testScore === score;
                        // Cap at 4 for new trainees - they need to prove exceptional (5) through actual work
                        const isCapped = score === 5;
                        const isDisabled = isCapped;
                        const tooltipId = `score-${fieldKey}-${score}`;
                        const criteriaText = criteria[score] || "No criteria defined";
                        const tooltipContent = isDisabled 
                          ? "Score 5 (Exceptional) requires proven work - cap at 4 for new trainees"
                          : `${criteria.label} - Score ${score}:\n${criteriaText}`;
                        
                        return (
                          <React.Fragment key={score}>
                            <button
                              onClick={() => !isDisabled && handleScoreChange(fieldKey, score)}
                              disabled={isDisabled}
                              data-tip={tooltipContent}
                              data-for={tooltipId}
                              className={`w-10 h-10 rounded-lg border-2 transition-all relative ${
                                isDisabled
                                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                                  : currentScore === score
                                  ? "bg-blue-600 text-white border-blue-600 font-bold"
                                  : isTestScore
                                  ? "bg-blue-50 text-blue-700 border-blue-300"
                                  : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                              }`}
                            >
                              {score}
                              {isTestScore && currentScore !== score && !isDisabled && (
                                <span className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
                              )}
                              {isDisabled && (
                                <span className="absolute -top-1 -right-1 text-xs">🔒</span>
                              )}
                            </button>
                            <ReactTooltip 
                              id={tooltipId} 
                              effect="solid" 
                              className="max-w-md whitespace-pre-line"
                              place="top"
                              multiline={true}
                            />
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Max score: 4 for new trainees (5 requires proven exceptional work)
                    </div>
                  </div>

                  {/* Show criteria for selected score */}
                  {currentScore && (
                    <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                      <div className="text-xs font-medium text-gray-700 mb-1">
                        Score {currentScore} Criteria:
                      </div>
                      <div className="text-sm text-gray-600 whitespace-pre-line">
                        {criteria[currentScore]}
                      </div>
                    </div>
                  )}

                  {/* Related Test Questions - Expandable */}
                  {hasTestResults && (() => {
                    const relatedTestCriteria = Object.entries(criterionMapping)
                      .filter(([_, evalCriterion]) => evalCriterion === fieldKey)
                      .map(([testCriterion]) => testCriterion);
                    
                    if (relatedTestCriteria.length > 0) {
                      const relatedQuestions = TRAINING_TEST_QUESTIONS.filter(q => 
                        relatedTestCriteria.includes(q.criterion)
                      );
                      const correctCount = relatedQuestions.filter(q => {
                        const answer = testAnswers[q.id];
                        return answer !== undefined && answer === q.correctAnswer;
                      }).length;
                      const isExpanded = expandedTestQuestions[fieldKey];

                      return (
                        <div className="mt-3 border-t border-gray-200 pt-3">
                          <button
                            onClick={() => toggleTestQuestions(fieldKey)}
                            className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-700">
                                Related Test Questions ({correctCount}/{relatedQuestions.length} correct)
                              </span>
                              {user.training_test_evaluation_scores?.[fieldKey] && (
                                <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                  Test Score: {user.training_test_evaluation_scores[fieldKey]}/5
                                </span>
                              )}
                            </div>
                            {isExpanded ? (
                              <FaChevronUp className="text-gray-500" />
                            ) : (
                              <FaChevronDown className="text-gray-500" />
                            )}
                          </button>

                          {isExpanded && (
                            <div className="mt-3 space-y-3">
                              {relatedQuestions.map((question) => {
                                const answer = testAnswers[question.id];
                                const isCorrect = answer !== undefined && answer === question.correctAnswer;
                                const selectedOption = answer !== undefined ? question.options[answer] : null;
                                const correctOption = question.options[question.correctAnswer];

                                return (
                                  <div
                                    key={question.id}
                                    className={`border-2 rounded-lg p-3 ${
                                      isCorrect ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
                                    }`}
                                  >
                                    <div className="flex items-start gap-2 mb-2">
                                      {isCorrect ? (
                                        <FaCheck className="text-green-600 mt-0.5" />
                                      ) : (
                                        <FaTimes className="text-red-600 mt-0.5" />
                                      )}
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="text-xs font-medium text-gray-600 uppercase">
                                            {question.criterion} • {question.type}
                                          </span>
                                        </div>
                                        <p className="text-sm font-medium text-gray-900">{question.question}</p>
                                      </div>
                                    </div>

                                    <div className="ml-6 space-y-2">
                                      <div>
                                        <div className="text-xs font-semibold text-gray-700 mb-1">Their Answer:</div>
                                        <div className={`p-2 rounded text-sm ${
                                          isCorrect ? "bg-green-100 border border-green-300" : "bg-red-100 border border-red-300"
                                        }`}>
                                          {selectedOption || "Not answered"}
                                        </div>
                                      </div>
                                      {!isCorrect && (
                                        <div>
                                          <div className="text-xs font-semibold text-green-700 mb-1">Correct Answer:</div>
                                          <div className="p-2 rounded text-sm bg-green-100 border border-green-300 text-green-800">
                                            {correctOption}
                                          </div>
                                        </div>
                                      )}
                                      {question.explanation && (
                                        <div className="p-2 bg-gray-100 rounded text-xs text-gray-700">
                                          <span className="font-semibold">Explanation: </span>
                                          {question.explanation}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Related Tickets/PRs - Expandable */}
                  {(() => {
                    const relatedTickets = getTicketsForCriterion(fieldKey);
                    
                    if (relatedTickets.length === 0) return null;
                    
                    const isExpanded = expandedTestQuestions[`${fieldKey}_tickets`];

                    return (
                      <div className="mt-3 border-t border-gray-200 pt-3">
                        <button
                          onClick={() => toggleTestQuestions(`${fieldKey}_tickets`)}
                          className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">
                              Related Tickets/PRs ({relatedTickets.length})
                            </span>
                          </div>
                          {isExpanded ? (
                            <FaChevronUp className="text-gray-500" />
                          ) : (
                            <FaChevronDown className="text-gray-500" />
                          )}
                        </button>

                        {isExpanded && (
                          <div className="mt-3 space-y-2">
                            {relatedTickets.map((ticket, idx) => (
                              <div key={idx} className="text-xs p-3 bg-blue-50 rounded border border-blue-200">
                                <div className="font-medium text-gray-900 mb-1">{ticket.title}</div>
                                {ticket.description && (
                                  <div className="text-gray-600 mb-2 line-clamp-2">{ticket.description}</div>
                                )}
                                {ticket.url && (
                                  <a 
                                    href={ticket.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-blue-600 hover:underline text-xs"
                                  >
                                    View ticket →
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Manager's Performance Prediction */}
      <div className="bg-white rounded-lg border-2 border-indigo-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Manager's Performance Prediction</h2>
          <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-medium">Required</span>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Based on the test scores, evaluation criteria, and training KPIs above, predict how this trainee will perform after graduation.
        </p>

        <div className="space-y-4">
          {/* Performance Prediction */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Post-Training Performance Prediction
            </label>
            <div className="grid grid-cols-4 gap-3">
              {[
                { value: 1, label: "Below Bar", color: "border-red-500 bg-red-50 text-red-700", description: "Will struggle, high risk" },
                { value: 2, label: "Meets Bar", color: "border-yellow-500 bg-yellow-50 text-yellow-700", description: "Adequate, needs monitoring" },
                { value: 3, label: "Above Bar", color: "border-blue-500 bg-blue-50 text-blue-700", description: "Strong performer" },
                { value: 4, label: "Overachiever", color: "border-green-500 bg-green-50 text-green-700", description: "Exceptional potential" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setPerformancePrediction(option.value)}
                  className={`p-4 border-2 rounded-lg transition-all text-left ${
                    performancePrediction === option.value
                      ? option.color + " font-semibold"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="text-sm font-medium mb-1">{option.label}</div>
                  <div className="text-xs text-gray-500">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Red Flags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Red Flags / Concerns (if any)
            </label>
            <textarea
              value={redFlags}
              onChange={(e) => setRedFlags(e.target.value)}
              placeholder="Any concerns about readiness, behaviors to watch, areas of risk..."
              className="w-full h-20 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
            />
          </div>

          {/* Standout Achievements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Standout Achievements (if any)
            </label>
            <textarea
              value={standoutAchievements}
              onChange={(e) => setStandoutAchievements(e.target.value)}
              placeholder="Exceptional work, fast learner moments, went above and beyond..."
              className="w-full h-20 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Notes</h2>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any additional context, observations, or recommendations..."
          className="w-full h-32 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}
